import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, Phone, Mail, Flag, ChevronRight, Loader2, BadgeCheck, Clock, ShieldCheck, Lock, Unlock, MessageCircle, FileText, Eye } from "lucide-react";
import { api, resolveImage } from "../lib/api";
import { categoryById } from "../lib/data";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ListingCard from "../components/ListingCard";
import CategoryIcon from "../components/CategoryIcon";
import ReviewsBlock, { RatingBadge } from "../components/ReviewsBlock";
import ImageCarousel from "../components/ImageCarousel";
import PdfViewerModal from "../components/PdfViewerModal";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const PLACEHOLDER = "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200";

const DETAIL_LABELS = {
  bedrooms: "Bedrooms", bathrooms: "Bathrooms", area_sqft: "Area", floor: "Floor", facing: "Facing",
  make: "Make", model: "Model", year: "Year", km_driven: "KM Driven", fuel_type: "Fuel", transmission: "Transmission",
  annual_turnover: "Turnover", established_year: "Established", reason_for_sale: "Reason for Sale",
  experience_years: "Experience (yrs)", service_type: "Service Type", min_budget: "Min Budget",
  availability: "Availability",
  // Education
  board: "Board", medium: "Medium", class_range: "Classes", stream: "Stream", affiliation: "Affiliation",
  subjects: "Subjects", batch_type: "Batch Type", age_range: "Age Range", timings: "Timings",
  courses: "Courses", certification: "Certification", mode: "Mode",
  principal_name: "Principal", num_teachers: "Teachers", exam_focus: "Exams / Courses",
  batch_timing: "Batch Timing", batch_size: "Batch Size", fees: "Fees",
  faculty_experience: "Faculty Experience", total_seats: "Total Seats",
  opening_time: "Opens", closing_time: "Closes", ac_type: "AC / Non-AC",
  home_tuition_available: "Home Tuition", approval: "Approval", total_faculty: "Total Faculty",
  phone_number: "Phone",
  // Healthcare
  doctor_name: "Doctor", whatsapp_number: "WhatsApp", opd_fees: "OPD Fees", ambulance_available: "Ambulance",
  specialization: "Specialization", specializations: "Specialization",
  pharmacy_type: "Pharmacy Type", home_delivery: "Home Delivery",
  home_collection: "Home Sample Collection", emergency_support: "24x7 Emergency", beds: "Beds",
  facilities: "Facilities", test_types: "Tests",
  name: "Name", open_hours: "Open Hours", mobile_number: "Mobile",
  email: "Email", website: "Website", consultation_fees: "Consultation Fees",
  total_beds: "Total Beds", available_24x7: "24x7 Available", therapy_types: "Therapy Types",
  services_offered: "Services", practice_types: "Practice",
  // Food
  food_type: "Food Type", cuisines: "Cuisines", seating_capacity: "Seating", outlets: "Food Outlets",
  specialties: "Specialties", layout: "Indoor / Outdoor", parking: "Parking", lawn_area: "Lawn Area",
  event_capacity: "Event Capacity", min_order: "Minimum Order",
  owner_name: "Owner", price_range: "Price Range",
  wifi_available: "WiFi", food_items: "Food Items", takeaway_available: "Takeaway",
  products: "Products", custom_cake_orders: "Custom Cakes", eggless_available: "Eggless",
  service_area_km: "Service Area (KM)", minimum_order: "Minimum Order",
  delivery_platforms: "Delivery Platforms", delivery_radius_km: "Delivery Radius (KM)",
  event_types: "Event Types", seating_capacity_event: "Seating",
  product_categories: "Products", specialty: "Specialty", fresh_stock_daily: "Fresh Daily",
  types: "Types", firmName: "Firm Name",
};

// Keys that must never be shown as a standalone row (they are merged into other rows).
const HIDDEN_KD_KEYS = new Set(["area_unit"]);

const BOOLEAN_KD_KEYS = new Set([
  "ambulance_available", "home_delivery", "home_collection", "emergency_support",
  "parking", "available_24x7", "wifi_available", "takeaway_available",
  "custom_cake_orders", "eggless_available", "fresh_stock_daily", "home_tuition_available",
]);

// Format a single key-detail value.
function formatKdValue(key, value, kd) {
  if (key === "area_sqft") {
    const unit = kd.area_unit && String(kd.area_unit).trim() ? kd.area_unit : "";
    return unit ? `${value} ${unit}` : String(value);
  }
  if (BOOLEAN_KD_KEYS.has(key) || typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (key === "opd_fees") {
    return `\u20B9 ${value}`;
  }
  return String(value);
}

const SELLER_LABEL = { owner: "Owner", broker: "Broker / Agent", agent: "Broker / Agent", builder: "Builder", dealer: "Dealer", professional: "Professional" };

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, patchUser } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [pdfViewer, setPdfViewer] = useState({ open: false, url: "", title: "" });
  const [contact, setContact] = useState(null); // { contactName, contactPhone, contactEmail, whatsappLink }
  const [expanded, setExpanded] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    setLoading(true);
    setContact(null);
    setActiveImg(0);
    window.scrollTo(0, 0);
    api.get(`/listings/${id}`)
      .then(async (r) => {
        setListing(r.data);
        // WhatsApp-only listings expose only the WhatsApp link — no phone/email
        // ever, no unlock flow. Contact block populated straight from the
        // detail response so the "Chat on WhatsApp" button works immediately.
        if (r.data.whatsappOnly && r.data.whatsappLink) {
          setContact({ contactName: r.data.contactName, contactPhone: null, contactEmail: null, whatsappLink: r.data.whatsappLink, whatsappNumber: r.data.whatsappNumber });
        } else if (r.data.contactUnlocked) {
          setContact({ contactName: r.data.contactName, contactPhone: r.data.contactPhone, contactEmail: r.data.contactEmail, whatsappLink: r.data.whatsappLink, whatsappNumber: r.data.whatsappNumber });
        }
        // Subscription-based privacy: no auto-reveal for any seller type.
        // Buyer must click "Unlock" and spend a credit for every listing.
      })
      .catch(() => setListing(false))
      .finally(() => setLoading(false));
  }, [id, user]);

  if (loading) return <div className="min-h-screen flex flex-col"><Header /><div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div><Footer /></div>;
  if (!listing) return <div className="min-h-screen flex flex-col"><Header /><div className="flex-1 flex flex-col items-center justify-center gap-4"><p className="text-muted-foreground">Listing not found.</p><Button asChild className="bg-primary text-white"><Link to="/listings">Browse Listings</Link></Button></div><Footer /></div>;

  const cat = categoryById(listing.category);
  const images = (listing.images && listing.images.length ? listing.images : [PLACEHOLDER]).map(resolveImage);
  const kd = listing.keyDetails || {};
  const kdEntries = Object.entries(kd).filter(([k, v]) => v != null && v !== "" && !Array.isArray(v) && !HIDDEN_KD_KEYS.has(k));
  const arrEntries = Object.entries(kd).filter(([k, v]) => Array.isArray(v) && v.length && !HIDDEN_KD_KEYS.has(k));
  const desc = listing.description || "";
  const isLong = desc.length > 300;
  const altBase = `${listing.title} - ${cat ? cat.label : ""} in ${listing.area || ""} ${listing.city}`.trim();

  const requireLogin = () => {
    toast.error("Please login to see seller contact");
    navigate("/login", { state: { from: window.location.pathname } });
  };

  const credits = user ? (user.credits ?? 0) : 0;

  const handleUnlock = async () => {
    if (!user) return requireLogin();
    setUnlocking(true);
    try {
      const { data } = await api.post(`/listings/${listing.id}/unlock`);
      setContact({ contactName: data.contactName, contactPhone: data.contactPhone, contactEmail: data.contactEmail, whatsappLink: data.whatsappLink, whatsappNumber: data.whatsappNumber });
      if (data.requiresCredit && !data.alreadyUnlocked) {
        patchUser({ credits: data.credits });
        toast.success(`Contact unlocked! ${data.credits} credits left.`);
      }
    } catch (e) {
      if (e.response?.status === 402) {
        toast.error("You need a plan to unlock seller contacts.");
        navigate("/pricing");
      } else {
        toast.error(e.response?.data?.detail || "Could not unlock contact.");
      }
    } finally {
      setUnlocking(false);
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: listing.title,
    description: desc.slice(0, 200),
    image: images,
    offers: listing.price ? { "@type": "Offer", price: listing.price, priceCurrency: "INR" } : undefined,
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Seo title={`${listing.title} — ${listing.area || listing.city} | The Pataudi`} description={desc.slice(0, 155)} image={images[0]} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Header />

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-6 w-full">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1 text-sm text-muted-foreground flex-wrap mb-5" data-testid="breadcrumb">
          <Link to="/" className="hover:text-primary">Home</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to={`/listings/${listing.category}`} className="hover:text-primary">{cat ? cat.label : listing.category}</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-ink truncate max-w-[180px]">{listing.area || listing.city}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
          {/* Left */}
          <article>
            <ImageCarousel
              images={images}
              alt={altBase}
              fallback={PLACEHOLDER}
              className="aspect-video border border-border"
              testid="gallery"
            />

            {/* PDF attachments (optional) */}
            {(listing.pdfs && listing.pdfs.length > 0) && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="listing-pdfs">
                {listing.pdfs.map((raw, i) => {
                  const url = resolveImage(raw) || raw;
                  const nice = decodeURIComponent((raw || "").split("/").pop() || `Document ${i + 1}`).slice(-38);
                  return (
                    <button
                      key={`${raw}-${i}`}
                      type="button"
                      onClick={() => setPdfViewer({ open: true, url, title: nice })}
                      className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 text-primary text-xs font-semibold px-3 py-1.5 hover:bg-primary/10 transition-colors"
                      data-testid={`listing-pdf-chip-${i}`}
                    >
                      <FileText className="h-3.5 w-3.5" /> <span className="truncate max-w-[160px]">{nice}</span> <Eye className="h-3.5 w-3.5" />
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-6">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-md"><CategoryIcon name={cat?.icon} className="h-3.5 w-3.5" /> {cat ? cat.label : listing.category}</span>
                {listing.subcategory && <span className="text-xs text-muted-foreground bg-surface border border-border px-2.5 py-1 rounded-md">{listing.subcategory}</span>}
                {(listing.verified_badge || listing.isVerifiedSeller) && (
                  <span
                    className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-md whitespace-nowrap"
                    style={{ background: "linear-gradient(135deg,#FFD700 0%,#B8860B 100%)", color: "#1a1a1a" }}
                    data-testid="detail-verified-badge"
                  >
                    <BadgeCheck className="h-3.5 w-3.5" /> Verified
                  </span>
                )}
              </div>
              <h1 className="mt-3 font-display font-bold text-2xl md:text-3xl text-ink">{listing.title}</h1>
              <p className="flex items-center gap-1.5 text-muted-foreground mt-2"><MapPin className="h-4 w-4" /> {listing.area ? `${listing.area}, ` : ""}{listing.city}</p>
            </div>

            {/* Key details */}
            {(kdEntries.length > 0 || arrEntries.length > 0) && (
              <section className="mt-6 bg-card rounded-card border border-border shadow-soft p-5" data-testid="key-details">
                <h2 className="font-display font-semibold text-ink mb-4">Key Details</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {kdEntries.map(([k, v]) => (
                    <div key={k}>
                      <p className="text-xs text-muted-foreground">{DETAIL_LABELS[k] || k}</p>
                      <p className="text-sm font-medium text-ink mt-0.5 break-words">{formatKdValue(k, v, kd)}</p>
                    </div>
                  ))}
                </div>
                {arrEntries.map(([k, v]) => (
                  <div key={k} className="mt-4">
                    <p className="text-xs text-muted-foreground mb-1.5">{DETAIL_LABELS[k] || k}</p>
                    <div className="flex flex-wrap gap-2">
                      {v.map((item, i) => <span key={i} className="text-xs bg-surface border border-border rounded-full px-2.5 py-1 text-ink">{item}</span>)}
                    </div>
                  </div>
                ))}
              </section>
            )}

            {/* Description */}
            <section className="mt-6" data-testid="description-section">
              <h2 className="font-display font-semibold text-ink mb-2">Description</h2>
              <p className={`text-sm text-ink/80 leading-relaxed whitespace-pre-line ${!expanded && isLong ? "line-clamp-3" : ""}`}>{desc}</p>
              {isLong && <button onClick={() => setExpanded(!expanded)} className="text-accent text-sm font-medium mt-2 hover:underline">{expanded ? "Show less" : "Read more"}</button>}
            </section>

            {/* Reviews & Ratings */}
            <ReviewsBlock listingId={listing.id} ownerId={listing.userId} currentUser={user} />

            <div className="mt-5 flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Posted {new Date(listing.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
              <span className="flex items-center gap-1 capitalize"><BadgeCheck className="h-3.5 w-3.5 text-success" /> {SELLER_LABEL[listing.sellerType] || listing.sellerType}</span>
            </div>
          </article>

          {/* Right - sticky contact */}
          <aside>
            <div className="lg:sticky lg:top-20 space-y-4">
              <div className="bg-card rounded-card border border-border shadow-soft p-5" data-testid="contact-card">
                <p className="font-display font-bold text-3xl text-primary">{listing.priceLabel || "Contact for Price"}</p>
                <span className="inline-block mt-1 text-xs font-medium capitalize bg-surface border border-border rounded-md px-2 py-0.5 text-muted-foreground">
                  {listing.priceType === "on_request" ? "On Request" : listing.priceType}
                </span>

                <div className="border-t border-border my-4" />
                <p className="text-xs text-muted-foreground">Posted by</p>
                <p className="font-medium text-ink">{listing.contactName || "Seller"}</p>
                <span className="inline-flex items-center gap-1 mt-1 text-xs font-medium text-success bg-success/10 px-2 py-0.5 rounded-md capitalize"><ShieldCheck className="h-3 w-3" /> {SELLER_LABEL[listing.sellerType] || listing.sellerType}</span>

                <div className="border-t border-border my-4" />

                {user ? (
                  contact ? (
                    <div className="space-y-2.5" data-testid="contact-unlocked">
                      {contact.contactPhone ? (
                        <div className="rounded-sm bg-success/5 border border-success/20 p-3">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-success flex items-center gap-1"><Unlock className="h-3.5 w-3.5" /> Seller contact</p>
                          <a href={`tel:${contact.contactPhone}`} className="mt-1.5 flex items-center gap-2 font-medium text-ink" data-testid="phone-revealed"><Phone className="h-4 w-4 text-primary" /> {contact.contactPhone}</a>
                          {contact.contactEmail && <a href={`mailto:${contact.contactEmail}`} className="mt-1 flex items-center gap-2 text-sm text-ink/80 break-all"><Mail className="h-4 w-4 text-primary" /> {contact.contactEmail}</a>}
                        </div>
                      ) : (
                        <div className="rounded-sm bg-primary/5 border border-primary/20 p-3" data-testid="whatsapp-only-notice">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-primary flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp Inquiry Only</p>
                          <p className="text-sm text-ink/80 mt-1">This seller accepts inquiries on WhatsApp. Click below to start a chat.</p>
                        </div>
                      )}
                      {contact.whatsappLink ? (
                        <a
                          href={`${contact.whatsappLink}?text=${encodeURIComponent(`Hi, I am interested in your listing "${listing.title}"`)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-sm bg-[#25D366] hover:bg-[#1ebe5d] text-white font-medium text-sm transition-colors"
                          data-testid="whatsapp-chat-btn"
                          aria-label="Send inquiry on WhatsApp"
                        >
                          <MessageCircle className="h-5 w-5" /> Send Inquiry on WhatsApp
                        </a>
                      ) : null}
                    </div>
                  ) : (
                    <div className="space-y-2.5" data-testid="owner-unlock-block">
                      {credits > 0 ? (
                        <>
                          <Button onClick={handleUnlock} disabled={unlocking} className="w-full bg-primary hover:bg-primary/90 text-white rounded-sm h-11 gap-2" data-testid="unlock-contact-btn">
                            {unlocking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4" /> Unlock Seller Contact</>}
                          </Button>
                          <p className="text-xs text-center text-muted-foreground">Uses 1 credit · <span className="font-medium text-ink">{credits} credits left</span></p>
                        </>
                      ) : (
                        <>
                          <div className="rounded-sm bg-amber-50 border border-amber-200 p-3 text-center">
                            <p className="text-sm font-medium text-amber-800">Contact is locked</p>
                            <p className="text-xs text-amber-700 mt-0.5">Pay to unlock any contact — buy a plan to continue.</p>
                          </div>
                          <Button onClick={() => navigate("/pricing")} className="w-full bg-accent hover:bg-accent/90 text-white rounded-sm h-11 gap-2" data-testid="buy-plan-btn"><Lock className="h-4 w-4" /> Buy a Plan to Unlock</Button>
                        </>
                      )}
                    </div>
                  )
                ) : (
                  <div className="space-y-2.5">
                    <Button onClick={requireLogin} className="w-full bg-primary hover:bg-primary/90 text-white rounded-sm h-11 gap-2" data-testid="login-to-contact-btn"><Lock className="h-4 w-4" /> Login to view contact</Button>
                    <p className="text-xs text-muted-foreground text-center">Contact details &amp; WhatsApp are visible to logged-in users.</p>
                  </div>
                )}

                <div className="border-t border-border my-4" />
                <button onClick={() => toast.success("Thanks! This listing has been reported for review.")} className="flex items-center justify-center gap-1.5 w-full text-sm text-muted-foreground hover:text-destructive transition-colors" data-testid="report-listing-btn"><Flag className="h-4 w-4" /> Report this listing</button>
              </div>
            </div>
          </aside>
        </div>

        {/* Similar */}
        {listing.similar && listing.similar.length > 0 && (
          <section className="mt-12" data-testid="similar-listings">
            <h2 className="font-display font-bold text-xl md:text-2xl text-ink mb-5">Similar Listings</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {listing.similar.map((l) => <ListingCard key={l.id} listing={l} />)}
            </div>
          </section>
        )}
      </main>

      <Footer />

      <PdfViewerModal
        open={pdfViewer.open}
        pdfUrl={pdfViewer.url}
        title={pdfViewer.title || `${listing.title} — PDF`}
        onClose={() => setPdfViewer({ open: false, url: "", title: "" })}
      />
    </div>
  );
}
